import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Input, message } from 'antd';
import apiService from '../../services/api';
import { ServiceType } from '../../services/api';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(
    135deg,
    #181A20 0%,
    #1a1d26 25%,
    #0f1115 50%,
    #1a1d26 75%,
    #181A20 100%
  );
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      circle at 20% 20%,
      rgba(14, 203, 129, 0.05) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 80%,
      rgba(14, 203, 129, 0.03) 0%,
      transparent 50%
    );
    pointer-events: none;
  }
`;

const Card = styled.div`
  width: 100%;
  max-width: 420px;
  padding: 40px;
  background-color: #1E2329;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  border: 1px solid #2B3139;
  position: relative;
  z-index: 1;
`;

const Logo = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;

  img {
    height: 60px;
    width: auto;
  }
`;

const Title = styled.h1`
  text-align: center;
  font-size: 24px;
  margin-bottom: 8px;
  color: #EAECEF;
`;

const Subtitle = styled.p`
  text-align: center;
  font-size: 14px;
  color: #848E9C;
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  color: #EAECEF;
  font-size: 14px;
  font-weight: 500;
`;

const ErrorText = styled.div`
  color: #F6465D;
  font-size: 12px;
  margin-top: 4px;
  min-height: 16px;
`;

const PrimaryButton = styled(Button)`
  width: 100%;
  height: 40px;
  background-color: #0ECB81 !important;
  border: none !important;
  font-weight: 600;
  color: #0B0E11 !important;
  transition: opacity 0.3s ease;

  &:hover {
    background-color: #0ECB81 !important;
    opacity: 0.8 !important;
  }

  &:focus {
    background-color: #0ECB81 !important;
    opacity: 1 !important;
  }
`;

const BackLink = styled.div`
  text-align: center;
  margin-top: 20px;
  font-size: 14px;
  color: #848E9C;

  a {
    color: ${({ theme }) => theme.colors.text.primary};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const StyledInput = styled(Input)`
  background-color: #2B3139 !important;
  border: 1px solid #474D57 !important;
  color: #EAECEF !important;

  &::placeholder {
    color: #848E9C !important;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary} !important;
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary} !important;
    box-shadow: 0 0 0 2px rgba(14, 203, 129, 0.1) !important;
  }

  input {
    background-color: transparent !important;
    color: #EAECEF !important;
  }
`;

const StyledPasswordInput = styled(Input.Password)`
  background-color: #2B3139 !important;
  border: 1px solid #474D57 !important;

  input {
    background-color: transparent !important;
    color: #EAECEF !important;
  }

  &::placeholder {
    color: #848E9C !important;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary} !important;
  }

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.primary} !important;
    box-shadow: 0 0 0 2px rgba(14, 203, 129, 0.1) !important;
  }

  .ant-input {
    background-color: transparent !important;
    color: #EAECEF !important;
  }

  .ant-input-suffix {
    color: #848E9C !important;
  }
`;

const coreService = apiService.getService(ServiceType.CORE);

const SetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get('email') ?? '';
  const codeParam = searchParams.get('code') ?? '';

  const [email, setEmail] = useState(emailParam);
  const [code, setCode] = useState(codeParam);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const [emailError, setEmailError] = useState('');
  const [codeError, setCodeError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');

  // Keep state in sync if URL params arrive after first render
  useEffect(() => {
    if (emailParam) setEmail(emailParam);
    if (codeParam) setCode(codeParam);
  }, [emailParam, codeParam]);

  const hasParams = Boolean(emailParam && codeParam);

  const validateEmail = (value: string) => {
    if (!value) {
      setEmailError('Email is required');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  const validateCode = (value: string) => {
    if (!value) {
      setCodeError('Verification code is required');
      return false;
    }
    setCodeError('');
    return true;
  };

  const validatePassword = (value: string) => {
    if (!value) {
      setPasswordError('Password is required');
      return false;
    }
    if (value.length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const validateConfirmPassword = (value: string) => {
    if (!value) {
      setConfirmPasswordError('Please confirm your password');
      return false;
    }
    if (value !== password) {
      setConfirmPasswordError('Passwords do not match');
      return false;
    }
    setConfirmPasswordError('');
    return true;
  };

  const handleSetPassword = async () => {
    const isEmailValid = validateEmail(email);
    const isCodeValid = validateCode(code);
    const isPwdValid = validatePassword(password);
    const isConfirmValid = validateConfirmPassword(confirmPassword);
    if (!isEmailValid || !isCodeValid || !isPwdValid || !isConfirmValid) return;

    setLoading(true);
    try {
      await coreService.post('/auth/reset-password', {
        email,
        code: code.trim(),
        newPassword: password,
      });
      message.success('Password set successfully! Please sign in.');
      navigate('/signin');
    } catch (error: any) {
      console.error('Set password error:', error);
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Failed to set password. The link may have expired.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Card>
        <Logo>
          <img src="/Algotrade - White.png" alt="Algotrade" />
        </Logo>
        <Title>Set Your Password</Title>
        <Subtitle>
          {hasParams
            ? `Welcome${emailParam ? `, ${emailParam}` : ''}! Choose a password to finish setting up your account.`
            : 'Enter your email and the code from your email to set your password.'}
        </Subtitle>

        {!hasParams && (
          <>
            <div style={{ marginBottom: '8px' }}>
              <Label>Email</Label>
              <StyledInput
                placeholder="Email address"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) validateEmail(e.target.value);
                }}
                onBlur={(e) => validateEmail(e.target.value)}
                size="large"
                status={emailError ? 'error' : ''}
              />
              <ErrorText>{emailError}</ErrorText>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <Label>Verification Code</Label>
              <StyledInput
                placeholder="Enter code"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                  if (codeError) validateCode(e.target.value);
                }}
                onBlur={(e) => validateCode(e.target.value)}
                size="large"
                status={codeError ? 'error' : ''}
              />
              <ErrorText>{codeError}</ErrorText>
            </div>
          </>
        )}

        <div style={{ marginBottom: '8px' }}>
          <Label>Password</Label>
          <StyledPasswordInput
            placeholder="Choose a password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (passwordError) validatePassword(e.target.value);
              if (confirmPassword && confirmPasswordError) validateConfirmPassword(confirmPassword);
            }}
            onBlur={(e) => validatePassword(e.target.value)}
            onPressEnter={handleSetPassword}
            size="large"
            status={passwordError ? 'error' : ''}
            autoFocus={hasParams}
          />
          <ErrorText>{passwordError}</ErrorText>
        </div>

        <div style={{ marginBottom: '8px' }}>
          <Label>Confirm Password</Label>
          <StyledPasswordInput
            placeholder="Confirm password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              if (confirmPasswordError) validateConfirmPassword(e.target.value);
            }}
            onBlur={(e) => validateConfirmPassword(e.target.value)}
            onPressEnter={handleSetPassword}
            size="large"
            status={confirmPasswordError ? 'error' : ''}
          />
          <ErrorText>{confirmPasswordError}</ErrorText>
        </div>

        <PrimaryButton onClick={handleSetPassword} loading={loading}>
          SET PASSWORD
        </PrimaryButton>

        <BackLink>
          Already set up? <Link to="/signin">Sign In</Link>
        </BackLink>
      </Card>
    </Container>
  );
};

export default SetPasswordPage;
